import React, { useEffect, useMemo, useState } from "react";
// import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { CircularProgress } from "@mui/material";
import { useIntl } from "react-intl";
import { Table } from "react-bootstrap";
import moment from "moment";
import { MenuItem, FormControl, Select } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import {
  WeekCounter,
  // addTwoValue,
  convertGBPtoPoundSymbol,
} from "../../../utils/commonFunction";
import SnackBarTool from "../../../../_metronic/_partials/widgets/snack-bar/SnackBar";
import { getCollectionExtraCharge } from "../_redux/order/orderCrud";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { fetchReturnReasons } from "../_redux/order/orderAction";

/**
 * The return / move-out step of the collection editor: the charges that apply
 * to the return, their total, and the reason for the return (with a
 * description when the chosen reason requires one).
 *
 * The reason options come from the backend and are loaded by the parent when
 * it mounts. If that load failed, or has not finished, the wizard asks again
 * when it opens, and if it still fails it says so and offers a retry, because
 * a reason is required and the agent could not otherwise finish the return.
 *
 * @param {Object} props
 * @param {Function} props.setDialogSave Closes or advances the parent dialog.
 * @param {Object} props.extraData Extra charge data for the return.
 * @param {boolean} props.loadingDialog Whether the parent is saving.
 * @param {Object} props.returnData The return's charge figures.
 * @param {Function} props.setReturnData Updates the return's charge figures.
 * @param {Object} props.formik The parent form, holding return_reason and return_reason_description.
 * @param {Object} props.orderDetail The order being edited.
 * @param {Function} props.setReturnChecks Updates which charges are applied.
 * @param {Object} props.returnChecks Which charges are applied.
 * @param {Function} props.sumDayProrated Sums the prorated extra-day storage charge.
 * @returns {JSX.Element} The return wizard.
 */
export const ReturnWizard = ({
  setDialogSave,
  extraData,
  loadingDialog,
  returnData,
  setReturnData,
  formik,
  orderDetail,
  setReturnChecks,
  returnChecks,
  sumDayProrated,
}) => {
  const intl = useIntl();

  // Options come from backend config (shared with the customer app), so the
  // wording can change without a release. The parent fetches them.
  const dispatch = useDispatch();
  const { returnReasons, returnReasonsError } = useSelector(
    (state) => ({
      returnReasons: state.order.returnReasons,
      returnReasonsError: state.order.returnReasonsError,
    }),
    shallowEqual
  );

  // The parent loads the list once, on mount. Ask again when the wizard
  // opens if that has not produced one, so a failed load does not leave
  // the agent stuck until the page is reloaded.
  useEffect(() => {
    if (!returnReasons?.length) dispatch(fetchReturnReasons());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Options for the dropdown: the current list, plus the order's existing
   * reason when it predates that list.
   *
   * Orders saved before this taxonomy hold values such as "Customer
   * Requested" that are no longer offered. Without carrying them, MUI
   * renders an out-of-range value as an empty Select and the agent sees
   * no reason at all on an order that has one.
   *
   * Keyed on the value the order was loaded with, not the current
   * selection: moving off a legacy reason must not remove it from the
   * list, or the agent cannot change their mind back to it.
   */
  const reasonOptions = useMemo(() => {
    const options = returnReasons || [];
    const stored = formik?.initialValues?.return_reason;
    if (!stored || options.some((reason) => reason?.label === stored)) {
      return options;
    }
    return [...options, { id: stored, label: stored, requires_comment: false }];
  }, [returnReasons, formik?.initialValues?.return_reason]);

  /**
   * Whether a reason obliges the agent to add a description.
   *
   * @param {string} label - The reason label, as stored on the order.
   * @returns {boolean} True when that reason requires a description.
   */
  const reasonRequiresDescription = (label) =>
    (returnReasons || []).some(
      (reason) => reason?.label === label && reason?.requires_comment
    );

  const reasonNeedsDescription = useMemo(
    () => reasonRequiresDescription(formik?.values?.return_reason),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [returnReasons, formik?.values?.return_reason]
  );

  /**
   * Record the chosen reason, dropping any description typed for a previous
   * one that no longer applies — it would otherwise be saved unseen.
   *
   * @param {object} event - Change event from the reason select.
   * @returns {void}
   */
  const handleReasonChange = (event) => {
    const label = event.target.value;
    // Set both in one update so validation runs once, against the final
    // pair — two calls would validate the new reason against the old
    // description and could flash an error that is already stale.
    formik.setValues({
      ...formik.values,
      return_reason: label,
      return_reason_description: reasonRequiresDescription(label)
        ? formik.values?.return_reason_description
        : "",
    });
  };

  const [formData, setFormData] = useState({ apply_on: "" });
  const [snack, setSnack] = useState({ call: false, type: "", message: "" });
  const [collectionExtraPrice, setCollectionExtraPrice] = useState(null);

  const calculateFinalPrice = () => {
    const extraReturnPrice = returnChecks?.is_return_cost
      ? Number(returnData?.extra_return_price || 0)
      : 0;
    const extraReturnCharge = returnChecks?.is_return_charge
      ? Number(returnData?.return_extra_charge || 0)
      : 0;
    const podPrice = !returnData?.return_extra_charge&&returnChecks?.is_storage_cost ? Number(returnData?.pod_price):0;
    const tollCharge = returnChecks?.is_return_congestion_charge
      ? Number(returnData?.toll_charge || 0)
      : 0;
    const depot_price = Number(returnData?.depot_return_charge || 0);
    const extraMilesTotalPrice = returnChecks?.is_extra_miles
      ? Number(returnData?.extraMiles?.totalPrice || 0)
      : 0;
    const discount = Number(returnData?.discount || 0);
    const pod_charge = returnChecks?.is_pod_charge
      ? Number(sumDayProrated(returnData?.extra_days_storage_charge))
      : 0;
     const extraDayCharge =  ((returnChecks?.is_extra_days_charge &&collectionExtraPrice?.price &&
        +collectionExtraPrice?.price) ||
        0)
    const totalPrice =
      depot_price +
      extraReturnPrice +
      extraReturnCharge +
      podPrice +
      extraMilesTotalPrice +
      pod_charge +
      extraDayCharge+
      tollCharge -
      discount;
    return totalPrice;
  };
  useEffect(() => {
    const timer = setTimeout(() => {
      setSnack({
        call: "",
        type: "",
        message: "",
      });
    }, 3000); // 3 seconds delay

    return () => {
      clearTimeout(timer); // Cleanup function to clear the timer if the component is unmounted
    };
  }, []);
  useEffect(() => {
    const data = {
      order_id: orderDetail?.id || "",
      type: "return",
      return_date: formik?.values?.return_date || "",
    };
    getCollectionExtraCharge(data).then((res) => {
      setCollectionExtraPrice(res.data.data);
      formik.setFieldValue("return_extra_days_charge", res.data.data?.price);
      formik.setFieldValue("return_extra_days_type", res.data.data?.type);
    });
  }, [formik?.values?.return_date]);
  console.log("returnData", returnData);
  const dataReturn = {
    extra_days_storage_charge: {
      mobile_storage_economy_plan: {
        week_prorated: "46.15",
        day_prorated: "36.16",
      },
      pod_protect: {
        week_prorated: "11.08",
        day_prorated: "8.68",
      },
    },
  };
  return (
    <div>
      {" "}
      {/* <button
        type="button"
        className={`btn  
          "btn-primaryCus" 
         font-weight-bolder font-size-sm mr-3 `}
        onClick={(e) => setDialogSave({ type: false, returnWizard: false })}
      >
        <ArrowBackIcon />
      </button> */}
      {extraData && (
        <div className="px-10 mt-6">
          <div className="mb-4">
            <span>
              <strong>{intl.formatMessage({ id: "Date.selected" })}:</strong>{" "}
              {formik?.values?.return_date
                ? moment(formik?.values?.return_date).format("DD-MM-YYYY")
                : ""}{" "}
              {formik?.values?.return_start_time_slot
                ? "- " + formik?.values?.return_start_time_slot
                : ""}
              {formik?.values?.return_start_time_slot &&
                formik?.values?.return_end_time_slot &&
                formik?.values?.return_start_time_slot !==
                  moment(
                    formik?.values?.return_date +
                      "T" +
                      formik?.values?.return_end_time_slot
                  )
                    .subtract(1, "hours")
                    .format("HH:mm") &&
                "-" +
                  moment(
                    formik?.values?.return_date +
                      "T" +
                      formik?.values?.return_end_time_slot
                  )
                    .subtract(1, "hours")
                    .format("HH:mm")}
            </span>
          </div>
          <div className="mb-4">
            <span>
              <strong>Return Address :</strong>{" "}
              {formik?.values?.return_address_line !== ""
                ? formik?.values?.return_address_line
                : formik?.values?.collection_address_line}
            </span>
          </div>
          {/* <div className="mb-4">
            <span>
              <strong>Next Billing Date :</strong> 12-07-2023
            </span>
          </div> */}
          <div className="mb-4">
            {formik?.values?.return_date &&
            orderDetail?.subscription_data?.next_billing_at &&
            new Date(formik?.values?.return_date).getTime() >
              Number(orderDetail?.subscription_data?.next_billing_at) * 1000 ? (
              <span>
                <strong>Extra Duration : </strong>

                {WeekCounter(
                  orderDetail?.collection_date,
                  formik?.values?.return_date
                ) < 14
                  ? 13 -
                    WeekCounter(
                      orderDetail?.collection_date,
                      formik?.values?.return_date
                    ) +
                    " week's"
                  : "0"}
              </span>
            ) : (
              ""
            )}
          </div>
          {/* {intl.formatMessage({ id: "Do.you.changes.booking" })} */}
          <div className="mt-6 mb-4">
            <strong>Charges applied</strong>
            {loadingDialog ? (
              <div className="text-center mt-8">
                <CircularProgress />
              </div>
            ) : (
              <Table responsive className="summerytable customColor">
                <thead>
                  <tr>
                    <th>Item</th>
                    <th>Price</th>
                  </tr>
                </thead>
                <tbody>
                  {returnData?.extra_return_price && (
                    <tr>
                      <td className="d-flex">
                        <div>
                          <label className="checkbox">
                            <input
                              type="checkbox"
                              name="is_cancel_charge"
                              checked={
                                returnChecks?.is_return_cost ? true : false
                              }
                              onChange={
                                (e) => {
                                  setReturnChecks({
                                    ...returnChecks,
                                    is_return_cost: e.target.checked ? 1 : 0,
                                  });
                                  if (
                                    !e.target.checked &&
                                    formData?.apply_on ===
                                      "discount_on_return_charge"
                                  ) {
                                    setFormData({
                                      ...formData,
                                      apply_on: "",
                                      discount: "",
                                    });
                                    setReturnData({
                                      ...returnData,
                                      discount: "",
                                    });
                                  }
                                }
                                // formik.setFieldValue(
                                //   "is_cancel_charge",
                                //   e.target.checked ? 1 : "0"
                                // )
                              }
                            />
                            <span className="mr-2"></span>
                          </label>
                        </div>
                        Return Cost:
                      </td>
                      <td>
                        {orderDetail?.currency
                          ? convertGBPtoPoundSymbol(orderDetail?.currency)
                          : "-"}
                        &nbsp;
                        {Number(+returnData.extra_return_price).toFixed(2)}
                      </td>
                    </tr>
                  )}
                  {/* {returnData?.return_extra_charge ? (
                    <tr>
                      <td className="d-flex">
                        <div>
                          <label className="checkbox">
                            <input
                              type="checkbox"
                              name="is_return_charge"
                              checked={
                                returnChecks?.is_return_charge ? true : false
                              }
                              onChange={
                                (e) => {
                                  setReturnChecks({
                                    ...returnChecks,
                                    is_return_charge: e.target.checked ? 1 : 0,
                                  });
                                  // if (
                                  //   !e.target.checked &&
                                  //   formData?.apply_on ===
                                  //     "discount_on_return_charge"
                                  // ) {
                                  //   setFormData({
                                  //     ...formData,
                                  //     apply_on: "",
                                  //     discount: "",
                                  //   });
                                  //   setReturnData({
                                  //     ...returnData,
                                  //     discount: "",
                                  //   });
                                  // }
                                }
                                // formik.setFieldValue(
                                //   "is_cancel_charge",
                                //   e.target.checked ? 1 : "0"
                                // )
                              }
                            />
                            <span className="mr-2"></span>
                          </label>
                        </div>
                        Return Extra Charge:
                      </td>
                      <td>
                        {orderDetail?.currency
                          ? convertGBPtoPoundSymbol(orderDetail?.currency)
                          : "-"}
                        &nbsp;
                        {Number(returnData.return_extra_charge).toFixed(2)}
                      </td>
                    </tr>
                  ):""} */}
                  {collectionExtraPrice?.price &&
                  formik?.values?.is_student_return != 1 ? (
                    <tr>
                      <td style={{display:"flex"}}>
                      <div>
                          <label className="checkbox">
                            <input
                              type="checkbox"
                              name="is_cancel_charge"
                              checked={
                                returnChecks?.is_extra_days_charge
                                  ? true
                                  : false
                              }
                              onChange={
                                (e) =>
                                  setReturnChecks({
                                    ...returnChecks,
                                    is_extra_days_charge: e.target
                                      .checked
                                      ? 1
                                      : 0,
                                  })
                                // formik.setFieldValue(
                                //   "is_cancel_charge",
                                //   e.target.checked ? 1 : "0"
                                // )
                              }
                            />
                            <span className="mr-2"></span>
                          </label>
                        </div>&nbsp;Extra Return{" "}
                        {collectionExtraPrice?.type}:
                      </td>
                      <td>
                        {" "}
                        {orderDetail?.currency
                          ? convertGBPtoPoundSymbol(orderDetail?.currency)
                          : "-"}
                        &nbsp;
                        {collectionExtraPrice?.price}
                      </td>
                    </tr>
                  ) : (
                    ""
                  )}
                  {typeof returnData?.pod_price !== "undefined" && (
                    <tr>
                      <td className="d-flex">
                      <div>
                          <label className="checkbox">
                            <input
                              type="checkbox"
                              name="is_cancel_charge"
                              checked={
                                returnChecks?.is_storage_cost
                                  ? true
                                  : false
                              }
                              onChange={
                                (e) =>
                                  setReturnChecks({
                                    ...returnChecks,
                                    is_storage_cost: e.target
                                      .checked
                                      ? 1
                                      : 0,
                                  })
                                // formik.setFieldValue(
                                //   "is_cancel_charge",
                                //   e.target.checked ? 1 : "0"
                                // )
                              }
                            />
                            <span className="mr-2"></span>
                          </label>
                        </div>
                        Storage Cost:
                      </td>
                      <td>
                        {" "}
                        {orderDetail?.currency
                          ? convertGBPtoPoundSymbol(orderDetail?.currency)
                          : "-"}
                        &nbsp;
                        {returnData.return_extra_charge
                          ? Number(
                              // returnData.pod_price +
                                Number(returnData.return_extra_charge)
                            ).toFixed(2)
                          :
                           Number(returnData.pod_price).toFixed(2)}
                      </td>
                    </tr>
                  )}
                  {console.log("returnData", returnData)}
                  {!returnData?.pod_price &&
                  (returnData?.extra_days_storage_charge?.plan ||
                    returnData?.extra_days_storage_charge?.addon) ? (
                    <tr>
                      <td className="d-flex">
                        <div>
                          <label className="checkbox">
                            <input
                              type="checkbox"
                              name="is_cancel_charge"
                              checked={
                                returnChecks?.is_pod_charge ? true : false
                              }
                              onChange={
                                (e) =>
                                  setReturnChecks({
                                    ...returnChecks,
                                    is_pod_charge: e.target.checked ? 1 : 0,
                                  })
                                // formik.setFieldValue(
                                //   "is_cancel_charge",
                                //   e.target.checked ? 1 : "0"
                                // )
                              }
                            />
                            <span className="mr-2"></span>
                          </label>
                        </div>
                        Extra Storage Cost:
                      </td>
                      <td>
                        {" "}
                        {orderDetail?.currency
                          ? convertGBPtoPoundSymbol(orderDetail?.currency)
                          : "-"}
                        &nbsp;
                        {sumDayProrated(returnData?.extra_days_storage_charge)}
                      </td>
                    </tr>
                  ) : (
                    ""
                  )}
                  {typeof returnData?.toll_charge !== "undefined" && (
                    <tr>
                      <td className="d-flex">
                        {" "}
                        <div>
                          <label className="checkbox">
                            <input
                              type="checkbox"
                              name="is_cancel_charge"
                              checked={
                                returnChecks?.is_return_congestion_charge
                                  ? true
                                  : false
                              }
                              onChange={
                                (e) =>
                                  setReturnChecks({
                                    ...returnChecks,
                                    is_return_congestion_charge: e.target
                                      .checked
                                      ? 1
                                      : 0,
                                  })
                                // formik.setFieldValue(
                                //   "is_cancel_charge",
                                //   e.target.checked ? 1 : "0"
                                // )
                              }
                            />
                            <span className="mr-2"></span>
                          </label>
                        </div>
                        Return Congestion Charge:
                      </td>
                      <td>
                        {" "}
                        {orderDetail?.currency
                          ? convertGBPtoPoundSymbol(orderDetail?.currency)
                          : "-"}
                        &nbsp;
                        {Number(returnData.toll_charge).toFixed(2)}
                      </td>
                    </tr>
                  )}
                  {returnData?.extraMiles?.id && (
                    <tr>
                      <td className="d-flex">
                        {" "}
                        <div>
                          <label className="checkbox">
                            <input
                              type="checkbox"
                              name="is_cancel_charge"
                              checked={
                                returnChecks?.is_extra_miles ? true : false
                              }
                              onChange={
                                (e) => {
                                  setReturnChecks({
                                    ...returnChecks,
                                    is_extra_miles: e.target.checked ? 1 : 0,
                                  });
                                  if (
                                    !e.target.checked &&
                                    formData?.apply_on === "discount_on_miles"
                                  ) {
                                    setFormData({
                                      ...formData,
                                      apply_on: "",
                                      discount: "",
                                    });
                                    setReturnData({
                                      ...returnData,
                                      discount: "",
                                    });
                                  }
                                }
                                // formik.setFieldValue(
                                //   "is_cancel_charge",
                                //   e.target.checked ? 1 : "0"
                                // )
                              }
                            />
                            <span className="mr-2"></span>
                          </label>
                        </div>
                        Extra Miles Cost:
                      </td>
                      <td>
                        {" "}
                        {orderDetail?.currency
                          ? convertGBPtoPoundSymbol(orderDetail?.currency)
                          : "-"}
                        &nbsp;
                        {Number(returnData.extraMiles.totalPrice).toFixed(2)}
                      </td>
                    </tr>
                  )}
                  {typeof returnData?.depot_return_charge !== "undefined" && (
                    <tr>
                      <td>Depot Return:</td>
                      <td>
                        {" "}
                        {orderDetail?.currency
                          ? convertGBPtoPoundSymbol(orderDetail?.currency)
                          : "-"}
                        &nbsp;
                        {Number(returnData.depot_return_charge).toFixed(2)}
                      </td>
                    </tr>
                  )}
                  <tr>
                    <td className="text-right">
                      <strong>Total Price:</strong>
                    </td>
                    <td>
                      <strong>
                        {" "}
                        {orderDetail?.currency
                          ? convertGBPtoPoundSymbol(orderDetail?.currency)
                          : "-"}
                        &nbsp;
                        {console.log("returnChecks", returnChecks)}
                        {Number(
                          ((returnChecks?.is_return_cost &&
                            +returnData?.extra_return_price) ||
                            0) +
                            ((returnChecks?.is_return_charge &&
                              +returnData?.return_extra_charge) ||
                              0) +
                              ((!returnChecks?.return_extra_charge&& returnChecks?.is_storage_cost && +returnData?.pod_price) || 0) +
                            ((returnChecks?.is_return_congestion_charge &&
                              +returnData?.toll_charge) ||
                              0) +
                            (+returnData?.depot_return_charge || 0) +
                            ((returnChecks?.is_pod_charge &&
                              Number(
                                sumDayProrated(
                                  returnData?.extra_days_storage_charge
                                )
                              )) ||
                              0) +
                            ((returnChecks?.is_extra_miles &&
                              +returnData?.extraMiles?.totalPrice) ||
                              0) +
                            ((returnChecks?.is_extra_days_charge &&collectionExtraPrice?.price &&
                              +collectionExtraPrice?.price) ||
                              0)
                        ).toFixed(2)}
                      </strong>
                    </td>
                  </tr>
                  {returnData?.discount && (
                    <>
                      <tr>
                        <td className="text-right">
                          <strong>Discount:</strong>
                        </td>
                        <td>
                          <strong>
                            {" "}
                            {orderDetail?.currency
                              ? convertGBPtoPoundSymbol(orderDetail?.currency)
                              : "-"}
                            &nbsp;
                            {Number(returnData?.discount).toFixed(2)}
                          </strong>
                        </td>
                      </tr>
                      <tr>
                        <td className="text-right">
                          <strong>Final Price:</strong>
                        </td>
                        <td>
                          <strong>
                            {" "}
                            {orderDetail?.currency
                              ? convertGBPtoPoundSymbol(orderDetail?.currency)
                              : "-"}
                            &nbsp;
                            {calculateFinalPrice().toFixed(2)}
                          </strong>
                        </td>
                      </tr>
                    </>
                  )}
                </tbody>
              </Table>
            )}
          </div>
          <div className="row">
            <div className="col-8">
              <div style={{ width: "31%" }}>
                <FormControl fullWidth>
                  <label>Apply Discount On</label>
                  <Select
                    MenuProps={{
                      anchorOrigin: {
                        vertical: "bottom",
                        horizontal: "left",
                      },
                      transformOrigin: {
                        vertical: "top",
                        horizontal: "left",
                      },
                      getContentAnchorEl: null,
                    }}
                    className="form-control"
                    IconComponent={(props) => (
                      <i {...props}>
                        <KeyboardArrowDownIcon />
                      </i>
                    )}
                    inputProps={{ "aria-label": "Without label" }}
                    displayEmpty
                    onChange={(e) => {
                      setSnack({
                        call: false,
                        type: "",
                        message: "",
                      });
                      if (e?.target?.value === "") {
                        setReturnData({ ...returnData, discount: "" });
                        setFormData({
                          ...formData,
                          apply_on: e.target.value,
                          discount: "",
                        });
                        return;
                      }
                      console.log("loghj", e?.target?.value);
                      
                      setFormData({
                        ...formData,
                        apply_on: e?.target?.value,
                      });
                    }}
                    // onBlur={handleBlur}
                    value={formData?.apply_on}
                  >
                    <MenuItem value="">Select Discount</MenuItem>
                    {returnChecks?.is_storage_cost &&
                      (returnData?.pod_price || returnData?.return_extra_charge) && (
                        <MenuItem value="discount_on_storage">
                          Apply discount on Storage Cost
                        </MenuItem>
                      )}
                    {returnChecks?.is_return_cost &&
                      returnData?.extra_return_price && (
                        <MenuItem value="discount_on_return_charge">
                          Apply discount on Return Charges
                        </MenuItem>
                      )}
                    {returnData.depot_return_charge && (
                      <MenuItem value="discount_on_depot_return_charge">
                        Apply discount on Depot Charges
                      </MenuItem>
                    )}
                    {returnChecks?.is_extra_miles &&
                      returnData?.extraMiles?.id && (
                        <MenuItem value="discount_on_miles">
                          Apply discount on Extra Miles
                        </MenuItem>
                      )}
                  </Select>
                </FormControl>
              </div>
              <div className="mt-2  d-flex">
                <div className="form-inline">
                  {" "}
                  <input
                    type="text"
                    className={`form-control`}
                    placeholder={"Amount"}
                    name="offerCode"
                    value={formData?.discount || ""}
                    // maxLength="40"
                    onChange={(e) => {
                      setSnack({
                        call: false,
                        type: "",
                        message: "",
                      });
                      console.log("formData",formData,returnData );
                      
                      if (
                        formData?.apply_on === "discount_on_return_charge" &&
                        Number(+returnData?.extra_return_price || 0) >=
                          Number(e.target.value)
                      ) {
                        setFormData({ ...formData, discount: e.target.value });
                      }
                      if (
                        formData?.apply_on === "discount_on_miles" &&
                        Number(+returnData?.extraMiles?.totalPrice || 0) >=
                          Number(e.target.value)
                      ) {
                        setFormData({ ...formData, discount: e.target.value });
                      }
                      if (
                        formData?.apply_on ===
                          "discount_on_depot_return_charge" &&
                        Number(+returnData?.depot_return_charge || 0) >=
                          Number(e.target.value)
                      ) {
                        setFormData({ ...formData, discount: e.target.value });
                      }
                      if (
                        formData?.apply_on ===
                          "discount_on_storage" && !returnData?.return_extra_charge &&
                        Number(+returnData?.pod_price || 0) >=
                          Number(e.target.value)
                      ) {
                        setFormData({ ...formData, discount: e.target.value });
                      }
                      if (
                        formData?.apply_on ===
                          "discount_on_storage" && returnData?.return_extra_charge &&
                        Number(+returnData?.return_extra_charge || 0) >=
                          Number(e.target.value)
                      ) {
                        setFormData({ ...formData, discount: e.target.value });
                      }
                    }}
                  />
                  {/* {orderUIContext?.state?.coupon?.some(
                (e) => e.name === formData?.offerCode
              ) && (
                <span style={{ color: "red" }}>
                  {intl.formatMessage({ id: "Already.used" })}
                </span>
              )} */}
                </div>

                <div className="ml-3">
                  <button
                    type="button"
                    onClick={() => {
                      if (
                        !returnData?.extra_return_price &&
                        !returnData?.extraMiles?.id &&
                        !returnData.depot_return_charge
                      ) {
                        setSnack({
                          call: true,
                          type: "error",
                          message:
                            "Discount cannot be applied on storage cost.",
                        });
                        return;
                      }
                      // setFormData({ ...formData, discount: "" });
                      setReturnData({
                        ...returnData,
                        discount: returnData?.discount
                          ? ""
                          : formData?.discount,
                        apply_on: formData?.apply_on,
                      });
                    }}
                    disabled={!formData?.apply_on}
                    className="btn btn-primary font-weight-bolder font-size-sm mr-3 sapply-code "
                    // disabled={
                    //   formData?.offerCode === "" ||
                    //   orderUIContext?.state?.coupon?.some(
                    //     (e) => e.name === formData?.offerCode
                    //   )
                    // }
                  >
                    {returnData?.discount ? "Remove" : "Add Discount"}
                  </button>
                </div>
              </div>
              <div className="form-inline mt-4">
                {/* <input
              type="text"
              // pattern="[0-9]{5}"
              // maxLength={"3"}
              className={`form-control offerCode mr-1 `}
              placeholder="Adhoc Charge"
              // value={formData?.adhocChargeName}
              // onChange={(e) => {
              //   setFormData({ ...formData, adhocChargeName: e.target.value });
              //   // orderUIContext.dispatchReducer({
              //   //   type: "adhocChargeName",
              //   //   value: e.target.value,
              //   // });
              // }}
            /> */}
                {/* </div> */}
                {/* <div className="form-inline"> */}
                {/* <input
              type="text"
              pattern="[0-9]{5}"
              maxLength={"5"}
              style={{ width: "72px" }}
              className={`form-control  ml-2`}
              placeholder="£ 0.00"
              // value={formData?.adhocPrice}
              // onChange={(e) => {
              //   if (e.target.value === "+" && e.target.value === "0") return;
              //   setFormData({ ...formData, adhocPrice: e.target.value });
              // }}
            /> */}
                {/* </div>
          <div className="form-inline" style={{ width: "100%" }}> */}
                {/* <button
              type="submit"
              // style={{ width: "27%" }}
              className="btn btn-primary font-weight-bolder font-size-sm mr-3  ml-2 "
              // onClick={handleAdHoc}
              // disabled={
              //   formData.adhocPrice && Number(formData.adhocPrice) > 0
              //     ? false
              //     : true
              // }
            >
              {intl.formatMessage({ id: "Add.Charge" })}
            </button> */}
              </div>
            </div>
            <div className="col-4">
              <FormControl fullWidth>
                <label>
                  <strong>Reason for Return</strong>
                </label>
                <Select
                  MenuProps={{
                    anchorOrigin: {
                      vertical: "bottom",
                      horizontal: "left",
                    },
                    transformOrigin: {
                      vertical: "top",
                      horizontal: "left",
                    },
                    getContentAnchorEl: null,
                  }}
                  className="form-control"
                  IconComponent={(props) => (
                    <i {...props}>
                      <KeyboardArrowDownIcon />
                    </i>
                  )}
                  inputProps={{ "aria-label": "Without label" }}
                  displayEmpty
                  // onChange={(e) => {
                  //   setFieldValue("search_view", e.target.value);
                  //   handleSubmit();
                  // }}
                  name="return_reason"
                  {...formik.getFieldProps("return_reason")}
                  onChange={handleReasonChange}
                >
                  <MenuItem value="">Select</MenuItem>
                  {reasonOptions.map((reason) => (
                    <MenuItem key={reason?.id} value={reason?.label}>
                      {reason?.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {returnReasonsError && !returnReasons?.length ? (
                <div className="fv-plugins-message-container">
                  <div className="fv-help-block">
                    The return reasons could not be loaded.{" "}
                    <button
                      type="button"
                      className="btn btn-link p-0 align-baseline"
                      onClick={() => dispatch(fetchReturnReasons())}
                    >
                      Try again
                    </button>
                  </div>
                </div>
              ) : null}
              {formik.touched.return_reason && formik.errors.return_reason ? (
                <div className="fv-plugins-message-container">
                  <div className="fv-help-block">
                    {formik.errors.return_reason}
                  </div>
                </div>
              ) : null}

              {reasonNeedsDescription ? (
                <>
                  <textarea
                    type="text"
                    name="return_reason_description"
                    placeholder={"Description"}
                    label="Desc"
                    className={`form-control mt-2  `}
                    maxLength={"255"}
                    {...formik.getFieldProps("return_reason_description")}
                  />
                  {formik.touched.return_reason_description &&
                  formik.errors.return_reason_description ? (
                    <div className="fv-plugins-message-container">
                      <div className="fv-help-block">
                        {formik.errors.return_reason_description}
                      </div>
                    </div>
                  ) : null}
                </>
              ) : null}
            </div>
            <div className="col-12">
            <span style={{color:"darkred", justifyContent:"end"}}>You no longer have to create ad-hoc charge as vault will automatically do that.</span>
            </div>
          </div>
        </div>
      )}
      {snack.call ? <SnackBarTool {...snack} /> : ""}
    </div>
  );
};
